// LE-00 (promo): クーポンのファネル集計（pure）。
// 取得ログ（coupon_usages）1 本から 配信→取得→利用 の人数と率を算出する。
// すべて決定論的 doc id（1 ユーザー 1 doc）前提のため、各カウント = ユニーク lineUserId 数。

import type { CouponUsage, Iso8601 } from '@kawai/shared';

export interface CouponFunnel {
  /** 配信人数（ユーザー単位の配信ログ = deliveredAt を持つ doc のユニーク件数 / broadcast は未計上）。 */
  delivered: number;
  /** 取得人数（status が claimed / redeemed のユニーク件数 / delivered のみは含めない）。 */
  claimed: number;
  /** 利用人数（1 回以上使用したユニーク lineUserId = status==='redeemed' の件数）。 */
  redeemedUsers: number;
  /** 利用回数（延べ = 各 usage の redeemCount の合計 / 旧 doc は redeemed を 1 回とみなす）。 */
  redeemedTotal: number;
  /** 取得率 = 取得人数 / 配信人数（配信 0 なら null）。0〜1。 */
  claimRate: number | null;
  /** 利用率 = 利用人数 / 取得人数（取得 0 なら null）。0〜1。 */
  redeemRate: number | null;
}

/** usage の利用回数を解釈（redeemCount 優先 / 旧 doc は redeemed を 1・未使用を 0）。 */
function redeemCountOf(u: Pick<CouponUsage, 'redeemCount' | 'status'>): number {
  if (typeof u.redeemCount === 'number') return u.redeemCount;
  return u.status === 'redeemed' ? 1 : 0;
}

/** 取得済み（claimed / redeemed）か。`delivered` のみ（配信したが未取得）は false。 */
export function isClaimedUsage(u: Pick<CouponUsage, 'status'>): boolean {
  return u.status === 'claimed' || u.status === 'redeemed';
}

/** 配信ログを持つ（セグメント配信で届けた）か。deliveredAt の有無で判定。 */
export function isDeliveredUsage(u: Pick<CouponUsage, 'deliveredAt'>): boolean {
  return typeof u.deliveredAt === 'string' && u.deliveredAt.length > 0;
}

/**
 * クーポンのファネル（配信 / 取得 / 利用 + 率）を集計する（pure / 非破壊）。
 * usages は 1 ユーザー 1 doc（決定論的 doc id）前提のため各カウント = ユニーク人数。
 *
 * - 配信: deliveredAt を持つ doc（セグメント配信のユーザー単位ログ）。OA broadcast は持たない。
 * - 取得: status が claimed / redeemed（delivered のみの doc は除外）。
 * - broadcast 経由の取得は配信ログを持たないため、取得 > 配信 になり得る（既知の制約）。
 */
export function summarizeCouponFunnel(usages: ReadonlyArray<CouponUsage>): CouponFunnel {
  const delivered = usages.filter(isDeliveredUsage).length;
  const claimed = usages.filter(isClaimedUsage).length;
  const redeemedUsers = usages.filter((u) => u.status === 'redeemed').length;
  const redeemedTotal = usages.reduce((sum, u) => sum + redeemCountOf(u), 0);
  return {
    delivered,
    claimed,
    redeemedUsers,
    redeemedTotal,
    claimRate: delivered > 0 ? claimed / delivered : null,
    redeemRate: claimed > 0 ? redeemedUsers / claimed : null,
  };
}

/** 率（0〜1）を `42.9%` 形式に整形（null は `-`）。 */
export function formatRate(rate: number | null): string {
  if (rate === null) return '-';
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * 顧客 1 人のクーポン横断アクティビティ（M-68 / 顧客一覧のバッジ日付 + フィルター判定用）。
 * クーポンの種類にかかわらず「取得 / 使用したことがあるか」と「その直近日時」を持つ。
 */
export interface UserCouponActivity {
  /** いずれかのクーポンを取得済み（claimed / redeemed）。 */
  claimed: boolean;
  /** いずれかのクーポンを店頭使用済み。 */
  redeemed: boolean;
  /** 直近の取得日時（全クーポンの `usedAt` の最大 / JST ISO8601）。 */
  lastClaimedAt?: Iso8601;
  /**
   * 直近の店頭使用日時（各 usage の `lastRedeemedAt` ?? `redeemedAt` の最大）。
   * 旧 doc は使用済みでも日時を持たないことがある（その場合 `redeemed: true` のまま undefined）。
   */
  lastRedeemedAt?: Iso8601;
}

/**
 * coupon_usages 全件から lineUserId → クーポン横断アクティビティを組み立てる（pure / M-68）。
 * `delivered` のみ（配信したが未取得）の doc は対象外。日時比較は JST ISO8601 の辞書順。
 */
export function buildUserCouponActivityMap(
  usages: ReadonlyArray<CouponUsage>,
): Map<string, UserCouponActivity> {
  const map = new Map<string, UserCouponActivity>();
  for (const u of usages) {
    const claimed = isClaimedUsage(u);
    const redeemed = redeemCountOf(u) > 0;
    if (!claimed && !redeemed) continue;
    const prev = map.get(u.lineUserId) ?? { claimed: false, redeemed: false };
    const next: UserCouponActivity = { ...prev };
    if (claimed) {
      next.claimed = true;
      if (u.usedAt && (!next.lastClaimedAt || u.usedAt > next.lastClaimedAt)) {
        next.lastClaimedAt = u.usedAt;
      }
    }
    if (redeemed) {
      next.redeemed = true;
      const at = u.lastRedeemedAt ?? u.redeemedAt;
      if (at && (!next.lastRedeemedAt || at > next.lastRedeemedAt)) {
        next.lastRedeemedAt = at;
      }
    }
    map.set(u.lineUserId, next);
  }
  return map;
}
